/** @file sampleAttachments: each attachment's own nodes sampled from the clip its driver's frame key selects. */
import type { AttachmentPlan } from '@zilo/pose3d-pose/attach/attachTypes';
import type { Rig } from '@zilo/pose3d-pose/rig/rigTypes';
import { buildChannels } from '@zilo/pose3d-pose/sample/buildChannels';
import type { LocalPose } from '@zilo/pose3d-pose/sample/localTypes';
import { sampleNode } from '@zilo/pose3d-pose/sample/sampleNode';
import { clipFrame } from '@zilo/pose3d-pose/time/clipFrame';

/**
 * Per plan: driver frame key `k` (0 without a driver); `1 <= k <= clips.length` samples clip `k - 1` at `timeSec` with
 * its own timing on the plan's own nodes only (host nodes are never written); otherwise they keep their locals.
 */
export function sampleAttachments(rig: Rig, plans: AttachmentPlan[], timeSec: number, locals: LocalPose): LocalPose {
  for (const plan of plans) {
    const k = plan.driver < 0 ? 0 : (locals[plan.driver]?.frameKey ?? 0);
    const clip = k >= 1 ? plan.clips[k - 1] : undefined;
    if (clip === undefined) continue;
    const channels = buildChannels(clip, rig);
    const time = clipFrame(clip, timeSec, rig.workTimeB);
    for (const i of plan.ownNodes) {
      const local = locals[i];
      if (local === undefined) continue;
      sampleNode(channels, i, time, local);
      if (rig.flex[i] !== 1) local.parent = -1;
    }
  }
  return locals;
}
